import * as Sentry from '@sentry/react';
import { MonitoringService, MonitoringConfig } from './monitoring-service';

export class SentryMonitoringService implements MonitoringService {
  private enabled = true;

  init(config: MonitoringConfig): void {
    this.enabled = config.enabled ?? true;

    if (!this.enabled) {
      return;
    }

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      debug: config.debug,
      sendDefaultPii: config.sendDefaultPii ?? true,
      integrations: [Sentry.replayIntegration(), Sentry.browserTracingIntegration()],
      replaysSessionSampleRate: config.sessionReplaySampleRate ?? 0.1,
      replaysOnErrorSampleRate: config.errorReplaySampleRate ?? 1.0,
      enableLogs: config.enableLogs ?? true,
    });
  }

  captureException(error: Error, context?: Record<string, unknown>): void {
    if (!this.enabled) {
      return;
    }

    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value as Sentry.Context);
        });
      }
      Sentry.captureException(error);
    });
  }

  captureMessage(
    message: string,
    level: 'info' | 'warning' | 'error' = 'info',
    context?: Record<string, unknown>,
  ): void {
    if (!this.enabled) {
      return;
    }

    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value as Sentry.Context);
        });
      }
      Sentry.captureMessage(message, level);
    });
  }

  setUser(user: { id: string; email?: string; username?: string }): void {
    if (!this.enabled) {
      return;
    }

    Sentry.setUser(user);
  }

  setContext(key: string, context: Record<string, unknown>): void {
    if (!this.enabled) {
      return;
    }

    Sentry.setContext(key, context);
  }

  addBreadcrumb(
    message: string,
    category = 'default',
    level: 'info' | 'warning' | 'error' = 'info',
  ): void {
    if (!this.enabled) {
      return;
    }

    Sentry.addBreadcrumb({
      message,
      category,
      level,
    });
  }
}
